import { ClientItemId } from 'owa-client-ids';
import { lazySetTriageActionAnnouncement } from 'owa-mail-store';
import { InstrumentationContext } from 'owa-search/lib/types/InstrumentationContext';
import { action, orchestrator, mutatorAction } from 'satcheljs';
import { addDatapointConfig } from 'owa-analytics-actions';
import { userMailInteractionAction } from 'owa-mail-actions';
import reactToItemService from '../services/reactToItemService';
import Message from 'owa-service/lib/contract/Message';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';

const reactToItem = action(
    'reactToItem',
    (
        message: Message,
        itemId: ClientItemId,
        conversationId: ClientItemId,
        reactionType: string,
        instrumentationContext: InstrumentationContext,
        actionAnnouncementString: string,
        ownerCurrentReactionType: string
    ) =>
        addDatapointConfig(
            {
                name: 'ReactToItem',
                actionSource: 'ReadingPane',
            },
            {
                message,
                itemId,
                conversationId,
                reactionType,
                instrumentationContext,
                actionAnnouncementString,
                ownerCurrentReactionType,
            }
        )
);

const updateMessageReactors = mutatorAction(
    'updateMessageReactors',
    (
        message: Message,
        newReactionType: string,
        userEmailAddress: string,
        userDisplayName: string,
        ownerCurrentReactionType: string
    ) => {
        // Initializing the array if needed
        if (!message.Reactions) {
            message.Reactions = [];
        }

        // GCI is not returning OwnerReactionType
        // Set it to what was passed in if needed
        message.OwnerReactionType = ownerCurrentReactionType;

        const isUnsetReaction = message.OwnerReactionType === newReactionType;
        let didAddReaction = false;
        message.Reactions.forEach(reaction => {
            // Check if the current user has already a reaction then remove
            if (reaction.ReactionType === message.OwnerReactionType) {
                reaction.Count--;
                reaction.Reactors = reaction.Reactors.filter(
                    reactor => reactor.ReactorEmail.EmailAddress !== userEmailAddress
                );

                // If we end up with 0 reaction count, then we have to nullify these, which means, no reactions
                if (reaction.Count === 0) {
                    reaction.Count = null;
                    reaction.Reactors = null;
                }
            }

            // Try add the current user into an existing reaction
            if (reaction.ReactionType === newReactionType && !isUnsetReaction) {
                didAddReaction = true;
                reaction.Count++;
                reaction.Reactors.push({
                    ReactorEmail: {
                        EmailAddress: userEmailAddress,
                        Name: userDisplayName,
                        MailboxType: 'Mailbox',
                        RoutingType: 'SMTP',
                    },
                });
            }
        });

        // If we did not add a reaction, create the reaction and add the the Reactor
        if (!didAddReaction && !isUnsetReaction) {
            message.Reactions.push({
                Count: 1,
                ReactionType: newReactionType,
                Reactors: [
                    {
                        ReactorEmail: {
                            EmailAddress: userEmailAddress,
                            Name: userDisplayName,
                            MailboxType: 'Mailbox',
                            RoutingType: 'SMTP',
                        },
                    },
                ],
            });
        }

        // In the end remove any Reaction that does not have any reactors
        message.Reactions = message.Reactions.filter(reactor => reactor.Reactors !== null);

        // Update the owner reaction type with the new reaction or unsetting the reaction
        message.OwnerReactionType = !isUnsetReaction ? newReactionType : null;
    }
);

orchestrator(reactToItem, actionMessage => {
    const {
        itemId,
        message,
        reactionType,
        conversationId,
        instrumentationContext,
        actionAnnouncementString,
        ownerCurrentReactionType,
    } = actionMessage;

    const userEmailAddress = getUserConfiguration().SessionSettings.UserEmailAddress;
    const userDisplayName = getUserConfiguration().SessionSettings.UserDisplayName;

    // Do the local update so we dont need to wait for Notification/GetItem to update locally
    updateMessageReactors(
        message,
        reactionType,
        userEmailAddress,
        userDisplayName,
        ownerCurrentReactionType
    );

    // Call the ServiceCommand to persist on the server
    // When unsetting the reaction, we need to send '0' to the server
    reactToItemService(itemId, message.OwnerReactionType !== null ? reactionType : '0');

    userMailInteractionAction('Reaction', [instrumentationContext], {
        itemIds: [itemId?.Id],
        conversationIds: [conversationId?.Id],
    });

    lazySetTriageActionAnnouncement.importAndExecute(actionAnnouncementString);
});

export default reactToItem;
